// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { DEFAULT_VALUES } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { UNSAFE_media } from "@atlaskit/primitives/responsive";

import {
    IndividualTabPropsQuote,
    DisplayProps
} from "@/components/wpl/tab-group/src";
import TabsQuote from "@/patterns/wpl/tabs-quote/src";

import {
    getAutomateTabsSectionImage,
    getPlanTabsSectionImage,
    getPlanTabsSectionVideo,
    getReleaseTabsSectionImage,
    getReportTabsSectionImage,
    getTrackTabsSectionImage
} from "../localized-images";

const TabsSection = () => {
    const intl = useIntl();

    const QUOTE_IMAGE_PROPS = {
        className: "tab-quote-image",
        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
        alt: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabs.quoteAlt",
            defaultMessage: "Quotation mark"
        })
    };

    const planLocalizedImageSrc = getPlanTabsSectionImage(intl.locale);
    const planLocalizedVideoSrc = getPlanTabsSectionVideo(intl.locale);
    const trackLocalizedImageSrc = getTrackTabsSectionImage(intl.locale);
    const releaseLocalizedImageSrc = getReleaseTabsSectionImage(intl.locale);
    const reportLocalizedImageSrc = getReportTabsSectionImage(intl.locale);
    const automateLocalizedImageSrc = getAutomateTabsSectionImage(intl.locale);

    const getPlanLocalizedMedia = (locale: string): DisplayProps => {
        // english should show a still image, other locales will show a localized gif (since the still has not been localized)
        if (locale === "en") {
            return {
                imageProps: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:49c215de-3b05-40f8-96e2-6dc65c70caa6/Board.png?cdnVersion=1187",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsPlan.imageAltText",
                        defaultMessage: "" // TODO - still awaiting what this should be
                    }),
                    aspectRatio: "none"
                }
            };
        }
        return {
            videoProps: {
                videoLink: planLocalizedVideoSrc,
                videoPreviewImage: planLocalizedImageSrc,
                aspectRatio: "none"
            }
        };
    };

    const planLocalizedMedia = getPlanLocalizedMedia(intl.locale);

    const tabs: IndividualTabPropsQuote[] = [
        {
            textLabel: "Plan",
            id: "tab-plan",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.plan",
                defaultMessage: "Plan"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.plan",
                defaultMessage:
                    "Break the big ideas down into manageable chunks across teams with user stories, issues, and tasks."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.plan",
                defaultMessage:
                    "Work becomes a lot more visible when it's all in one place. It makes collaboration a whole lot easier."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.plan",
                defaultMessage: "Jeff Lai"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.plan"
                    defaultMessage="Internal Infrastructure, canva"
                    values={DEFAULT_VALUES}
                />
            ),
            display: planLocalizedMedia
        },
        {
            textLabel: "Track",
            id: "tab-track",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.track",
                defaultMessage: "Track"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.track",
                defaultMessage:
                    "Prioritize and discuss your team’s work in context with complete visibility at every level."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.track",
                defaultMessage:
                    "The pace of delivery from idea to reality has exploded."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.track",
                defaultMessage: "Greg Franklin"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.track"
                    defaultMessage="Agile lead, Cancer Research UK"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: trackLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.track",
                        defaultMessage:
                            "The timeline view in Jira allows users to review progress by sprints in weeks, months, or quarters. Status is by category in this example."
                    })
                }
            }
        },
        {
            textLabel: "Release",
            id: "tab-release",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.release",
                defaultMessage: "Release"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.release",
                defaultMessage:
                    "Ship faster, with confidence knowing the information you have is always up-to-date."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.release",
                defaultMessage:
                    "Less friction in the development process means better results and a faster time to market."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.release",
                defaultMessage: "Louis Bennett"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.release"
                    defaultMessage="Director of Engineering, Trulia"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: releaseLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.release",
                        defaultMessage:
                            "Releases within a Jira project. This view contains the version, status, progress, start date, release date, and description."
                    })
                }
            }
        },
        {
            textLabel: "Report",
            id: "tab-report",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.report",
                defaultMessage: "Report"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.report",
                defaultMessage:
                    "Improve team performance in context, based on real-time, visual data."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.report",
                defaultMessage:
                    "Full visibility in Jira across the whole organization enables you to make better management judgment. "
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.report",
                defaultMessage: "Paul Leaon"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.report"
                    defaultMessage="Digital Portfolio Director, Inchcape"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: reportLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.report",
                        defaultMessage:
                            "Within the Kanban board in Jira, a project backlog shows 5 issues are on the board and 8 are in backlog."
                    })
                }
            }
        },
        {
            textLabel: "Automate",
            id: "tab-automate",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.automate",
                defaultMessage: "Automate"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.automate",
                defaultMessage:
                    "Save time, keep your team focused, and help work flow with no-code automation."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.automate",
                defaultMessage:
                    "We’re saving 150 hours+ of valuable engineering time per month. "
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.automate",
                defaultMessage: "Jeff Lai"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.automate"
                    defaultMessage="Internal Infrastructure, canva"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: automateLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.automate",
                        defaultMessage:
                            "Automation workflow in Jira. An example automation closes all stories in an epic when the epic is completed."
                    })
                }
            }
        }
    ];

    return (
        <section
            css={css`
                letter-spacing: 0.3px;

                .software-jira-tabs {
                    background-image: linear-gradient(
                        123.5deg,
                        #091e42 78%,
                        #0747a6 99.98%
                    );
                    background-position: center;
                    background-repeat: no-repeat;
                    background-size: cover;
                }

                .tab-group-inner {
                    gap: 8px;

                    button {
                        padding: 4px 16px;
                        border: 1px solid transparent;
                        transition: all 0.25s;

                        &:hover {
                            border: 1px solid #c1c7d0;
                        }

                        &:focus-visible {
                            outline: revert;
                        }

                        ${UNSAFE_media.below.md} {
                            font-size: 1.25rem;
                        }
                    }

                    [aria-selected="true"] {
                        &:hover {
                            border-color: transparent;
                        }
                    }
                }

                .select-container {
                    .row-layout {
                        padding: 0;
                    }
                }

                .select-container-inner {
                    min-width: 300px;
                    max-width: 388px;
                    width: 100%;
                    margin: auto;
                }

                #tabs-section {
                    border: none;
                    font-size: 1rem;
                    line-height: 40px;
                    padding: 0 15px;
                    width: 100%;

                    &:focus-visible {
                        outline: revert;
                    }
                }

                .content-block {
                    width: 100%;
                }

                .tab-quote-image {
                    width: 45px;
                }

                .tab-panel {
                    padding: 16px;

                    .row-layout {
                        align-items: flex-start;
                        padding: 0;
                        max-width: 1440px;
                        width: 100%;

                        grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);

                        ${UNSAFE_media.below.md} {
                            grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
                        }

                        ${UNSAFE_media.below.sm} {
                            display: flex;
                            flex-direction: column-reverse;
                            grid-template-columns: minmax(0, 1fr);
                        }
                    }
                }

                .text-section {
                    h2,
                    h3 {
                        font-size: 2.25rem;
                        margin: 0;
                    }

                    h3,
                    p {
                        font-family: var(--bxp-charlie-display-font-family);
                        margin-bottom: 24px;
                    }
                    p {
                        font-size: 1.25rem;
                        font-weight: normal;
                        letter-spacing: 0.3px;
                        line-height: 1.4;
                    }

                    ${UNSAFE_media.below.sm} {
                        h2,
                        h3 {
                            font-size: 1.5rem;
                        }
                    }
                }

                :last-child {
                    ${UNSAFE_media.below.md} {
                        display: none;
                    }
                }

                .quote-wrapper {
                    border-radius: 0px 150px 150px 0px;

                    ${UNSAFE_media.below.md} {
                        display: none;
                    }
                }

                .quote-blurb {
                    font-size: 1.25rem;
                    line-height: 1.2;
                }

                .quote-name {
                    font-size: 0.875rem;
                    line-height: 1.25;
                    width: 75%;
                }

                .quote-company {
                    font-size: 0.75rem !important;
                    line-height: 2;
                    width: 75%;
                }

                p {
                    font-size: 1.25rem;
                    line-height: 1.2;
                }
            `}
        >
            <TabsQuote
                id="tabs-section"
                label="Tabs"
                headerText={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.tabsHeader",
                    defaultMessage: "All from a single source of truth"
                })}
                bodyText=""
                tabs={tabs}
                colors={{
                    defaultBG: "n600",
                    defaultText: "n60",
                    activeBG: "g200",
                    activeText: "n900",
                    hoverBG: "n600",
                    hoverText: "n60",
                    quoteColor: "b300"
                }}
                className="software-jira-tabs"
                backgroundProperties={{
                    textColor: "n0"
                }}
            />
        </section>
    );
};

export default TabsSection;
